/* iter55g26/iter55h01 — Cross-paragraph selection helper.
 *
 * Each paragraph inside `ManuscriptBody` is its own `<p contentEditable>`
 * element. Browsers restrict Cmd/Ctrl+A to the currently-focused element,
 * so writers can't "Select All → Copy/Delete" the whole chapter.
 *
 * v2 additions:
 *   • Proper cross-paragraph Delete (calls onCrossParaDelete callback)
 *   • One-level Ctrl+Z undo of cross-para deletion
 *   • Paste-Replace: native Ctrl+V or loom:paste-replace event replaces
 *     a cross-paragraph selection with clipboard text
 *
 * Usage:
 *   useCrossParagraphSelection(proseRef, { onCrossParaDelete, onUndoCrossParaDelete });
 */
import { useEffect, useRef } from 'react';

function collectParagraphTexts(container) {
  const paras = container.querySelectorAll('p[data-testid^="manuscript-para-"]');
  return Array.from(paras);
}

function selectAllParagraphs(container) {
  const paras = collectParagraphTexts(container);
  if (paras.length === 0) return false;
  const first = paras[0];
  const last  = paras[paras.length - 1];
  const range = document.createRange();
  try {
    range.setStart(first, 0);
    range.setEnd(last, last.childNodes.length);
  } catch {
    return false;
  }
  const sel = window.getSelection();
  if (!sel) return false;
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}

function selectionSpansMultipleParagraphs(container) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  const anchor = range.startContainer;
  const focus  = range.endContainer;
  if (!container.contains(anchor) || !container.contains(focus)) return false;
  const closestP = (n) => (n?.nodeType === 1 ? n : n?.parentElement)?.closest('p[data-testid^="manuscript-para-"]');
  const a = closestP(anchor);
  const b = closestP(focus);
  return Boolean(a && b && a !== b);
}

function joinedSelectionText(container) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return '';
  const range = sel.getRangeAt(0);
  const paras = collectParagraphTexts(container);
  const parts = [];
  for (const p of paras) {
    if (!range.intersectsNode(p)) continue;
    const sub = range.cloneRange();
    try {
      if (sub.startContainer !== p && !p.contains(sub.startContainer)) sub.setStart(p, 0);
      if (sub.endContainer !== p && !p.contains(sub.endContainer)) sub.setEnd(p, p.childNodes.length);
    } catch { continue; }
    const text = sub.toString();
    if (text) parts.push(text);
  }
  return parts.join('\n\n');
}

// Compute which paragraphs the selection touches and what text to keep/remove.
// Returns null if it's a single-para selection or selection is outside container.
function computeCrossParaDelete(container, sel) {
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  const paras = collectParagraphTexts(container);
  const selected = paras.filter((p) => range.intersectsNode(p));
  if (selected.length < 2) return null;

  const firstPara = selected[0];
  const lastPara  = selected[selected.length - 1];
  const firstIdx  = paras.indexOf(firstPara);
  const lastIdx   = paras.indexOf(lastPara);

  // Text to keep BEFORE the selection start (in first paragraph)
  let firstKeepText = '';
  try {
    const r = document.createRange();
    r.setStart(firstPara, 0);
    r.setEnd(range.startContainer, range.startOffset);
    firstKeepText = r.toString();
  } catch { /* keep empty */ }

  // Text to keep AFTER the selection end (in last paragraph)
  let lastKeepText = '';
  try {
    const r = document.createRange();
    r.setStart(range.endContainer, range.endOffset);
    r.setEnd(lastPara, lastPara.childNodes.length);
    lastKeepText = r.toString();
  } catch { /* keep empty */ }

  return { firstIdx, lastIdx, firstKeepText, lastKeepText };
}

export default function useCrossParagraphSelection(containerRef, opts = {}) {
  // Keep opts stable without re-registering listeners on every render.
  const optsRef = useRef(opts);
  useEffect(() => { optsRef.current = opts; });

  // Track whether the last action was a cross-para delete so Ctrl+Z can undo it.
  const crossParaDeletePendingRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // ── Keyboard shortcuts ────────────────────────────────────────────────
    const onKeyDown = (e) => {
      // Cmd/Ctrl+A → select entire manuscript body.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey
          && (e.key === 'a' || e.key === 'A')) {
        if (!container.contains(document.activeElement)
            && !container.contains(window.getSelection()?.anchorNode)) return;
        const ok = selectAllParagraphs(container);
        if (ok) {
          e.preventDefault();
          const count = collectParagraphTexts(container).length;
          if (count > 1) {
            window.dispatchEvent(new CustomEvent('loom:select-all-para', { detail: { count } }));
          }
        }
      }

      // Cmd/Ctrl+Z → undo last cross-paragraph delete (one level).
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey
          && (e.key === 'z' || e.key === 'Z')) {
        if (crossParaDeletePendingRef.current && optsRef.current.onUndoCrossParaDelete) {
          e.preventDefault();
          e.stopPropagation();
          crossParaDeletePendingRef.current = false;
          optsRef.current.onUndoCrossParaDelete();
        }
        // else: fall through to browser/editor built-in undo
      }
    };

    // Any normal typing inside the prose clears the undo snapshot.
    const onInput = () => { crossParaDeletePendingRef.current = false; };

    // ── Right-click context menu "Select All" ─────────────────────────────
    const onSelectAllEvent = () => {
      const ok = selectAllParagraphs(container);
      if (ok) {
        const count = collectParagraphTexts(container).length;
        window.dispatchEvent(new CustomEvent('loom:select-all-para', { detail: { count } }));
      }
    };

    // ── Right-click context menu "Delete" / loom:delete-selection ─────────
    const onDeleteSelectionEvent = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
      const range = sel.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) return;

      const paras = collectParagraphTexts(container);
      const selected = paras.filter((p) => range.intersectsNode(p));
      if (selected.length === 0) return;

      // Single paragraph — native range.deleteContents() works fine here.
      if (selected.length === 1) {
        range.deleteContents();
        selected[0].dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }

      // Cross-paragraph deletion — native deleteContents() cannot cross
      // separate contentEditable boundaries; use React state callback.
      const data = computeCrossParaDelete(container, sel);
      if (!data) return;

      const { firstIdx, lastIdx, firstKeepText, lastKeepText } = data;
      const mergedText = (firstKeepText + lastKeepText).trim();

      // ⚠️  Must call onBeforeCrossParaDelete BEFORE clearing the selection.
      // sel.removeAllRanges() blurs all selected paragraphs synchronously,
      // which triggers onBlur → saveParagraphEdit on every one. Those saves
      // race with crossParaDelete and corrupt the chapter if they run after
      // crossParaDelete has already rebuilt the paragraphs array.
      optsRef.current.onBeforeCrossParaDelete?.();
      sel.removeAllRanges();
      crossParaDeletePendingRef.current = true;
      optsRef.current.onCrossParaDelete?.(firstIdx, mergedText, lastIdx);
    };

    // ── Ctrl/Cmd + left-click → select entire chapter ─────────────────────
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.shiftKey || e.altKey) return;
      if (!container.contains(e.target)) return;
      const ok = selectAllParagraphs(container);
      if (ok) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // ── Copy / Cut ────────────────────────────────────────────────────────
    const onCopyOrCut = (e) => {
      if (!selectionSpansMultipleParagraphs(container)) return;
      const text = joinedSelectionText(container);
      if (!text) return;
      e.preventDefault();
      try { e.clipboardData.setData('text/plain', text); } catch { /* Safari */ }
      if (e.type === 'cut') {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const data = computeCrossParaDelete(container, sel);
          if (data) {
            const { firstIdx, lastIdx, firstKeepText, lastKeepText } = data;
            optsRef.current.onBeforeCrossParaDelete?.();
            sel.removeAllRanges();
            crossParaDeletePendingRef.current = true;
            optsRef.current.onCrossParaDelete?.( firstIdx, (firstKeepText + lastKeepText).trim(), lastIdx);
          } else {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            const anchorP = (sel.anchorNode?.nodeType === 1 ? sel.anchorNode : sel.anchorNode?.parentElement)
              ?.closest('p[data-testid^="manuscript-para-"]');
            anchorP?.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      }
    };

    // ── Paste with cross-paragraph selection (native Ctrl+V) ──────────────
    const onNativePaste = (e) => {
      if (!selectionSpansMultipleParagraphs(container)) return;
      const clipText = e.clipboardData?.getData('text/plain') || '';
      const sel = window.getSelection();
      const data = computeCrossParaDelete(container, sel);
      if (!data || !optsRef.current.onCrossParaDelete) return;
      e.preventDefault();
      const { firstIdx, lastIdx, firstKeepText, lastKeepText } = data;
      const mergedText = (firstKeepText + clipText + lastKeepText).trim();
      optsRef.current.onBeforeCrossParaDelete?.();
      sel.removeAllRanges();
      crossParaDeletePendingRef.current = false;
      optsRef.current.onCrossParaDelete(firstIdx, mergedText, lastIdx);
    };

    // ── loom:paste-replace — from right-click menu "Paste" ───────────────
    const onPasteReplace = (e) => {
      const clipText = e.detail?.text || '';
      const sel = window.getSelection();

      if (!sel || sel.isCollapsed || !selectionSpansMultipleParagraphs(container)) {
        // No cross-para selection — just insertText at cursor
        if (clipText) document.execCommand('insertText', false, clipText);
        return;
      }

      const data = computeCrossParaDelete(container, sel);
      if (!data || !optsRef.current.onCrossParaDelete) {
        if (clipText) document.execCommand('insertText', false, clipText);
        return;
      }

      const { firstIdx, lastIdx, firstKeepText, lastKeepText } = data;
      const mergedText = (firstKeepText + clipText + lastKeepText).trim();
      optsRef.current.onBeforeCrossParaDelete?.();
      sel.removeAllRanges();
      crossParaDeletePendingRef.current = false;
      optsRef.current.onCrossParaDelete(firstIdx, mergedText, lastIdx);
    };

    document.addEventListener('keydown',   onKeyDown,   true);
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('copy',      onCopyOrCut, true);
    document.addEventListener('cut',       onCopyOrCut, true);
    document.addEventListener('paste',     onNativePaste, true);
    container.addEventListener('input',    onInput,     true);
    window.addEventListener('loom:select-all-chapter', onSelectAllEvent);
    window.addEventListener('loom:delete-selection',   onDeleteSelectionEvent);
    window.addEventListener('loom:paste-replace',      onPasteReplace);

    return () => {
      document.removeEventListener('keydown',   onKeyDown,   true);
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('copy',      onCopyOrCut, true);
      document.removeEventListener('cut',       onCopyOrCut, true);
      document.removeEventListener('paste',     onNativePaste, true);
      container.removeEventListener('input',    onInput,     true);
      window.removeEventListener('loom:select-all-chapter', onSelectAllEvent);
      window.removeEventListener('loom:delete-selection',   onDeleteSelectionEvent);
      window.removeEventListener('loom:paste-replace',      onPasteReplace);
    };
  }, [containerRef]); // optsRef is always current via the sync effect above
}
